import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash/debounce'
import { watch } from 'vue'

import { createApp } from '@app/adapters/create-app'
import { SfLog } from '@app/adapters/logger'
import { fetchColumnsPreviewRequest } from '@app/application/report/api/columns'
import ColumnEditorDrawer from '@app/application/report/column-editor/components/column-editor-drawer.vue'
import {
  blankReportColumns,
  findReportTable,
  renderPreviewColumns,
  restoreReportColumns,
} from '@app/application/report/column-editor/report-column-preview'
import { useColumnEditor } from '@app/application/report/column-editor/use-column-editor'
import getPiniaInstance from '@app/shared/pinia/instance'

// Long enough that dragging a row or clicking through a dropdown settles into a
// single request, short enough that one edit still reads as immediate.
const PREVIEW_DEBOUNCE_MS = 250

// The frame the report table is rendered into (reports/show.html.slim). It loads
// lazily, so the table can arrive after the drawer is already open.
const TABLE_FRAME_ID = 'report-table'

export default class extends Controller {
  static targets = ['drawer']

  private drawerTarget!: HTMLElement

  private app: ReturnType<typeof createApp> | null = null

  private store: ReturnType<typeof useColumnEditor> | null = null

  private stopWatchers: (() => void)[] = []

  // Only the newest preview counts: edits outpace the responses, and an earlier
  // one landing last would leave the table showing a config the drawer has
  // already moved on from.
  private previewRequest = 0

  private previewRendered = false

  connect(): void {
    document.addEventListener('turbo:frame-load', this.reapplyPreview)
  }

  // Triggered by the subnav button, which carries the report id and the period
  // set currently on screen as action params
  // (`data-reports--column-editor-report-id-param` and
  // `-report-period-set-id-param`).
  open(
    event: Event & {
      params: { reportId: number; reportPeriodSetId?: number }
    }
  ): void {
    const store = this.mountDrawer()

    // The subnav button stays clickable behind the open drawer, and clicking it
    // again means nothing: the store is already configured and open, so all a
    // second pass would do is blank the table and rebuild every preview cell to
    // arrive back where it started.
    if (store.isOpen) return

    // A report is always viewed through some period set, but only an explicitly
    // selected one is rendered as a param.
    store.configure({
      reportId: event.params.reportId,
      reportPeriodSetId: event.params.reportPeriodSetId ?? null,
    })
    store.open()

    // The report keeps its own columns until there is a preview to put in their
    // place — hiding them up front empties the table for as long as the first
    // request takes, which reads as the report having been wiped. They do go
    // quiet straight away, so the preview arriving only moves columns about
    // instead of also blanking every figure at that moment.
    blankReportColumns(findReportTable())

    // Scheduled rather than fired: loading the columns changes them, which the
    // watcher below also schedules a preview for, and one request is enough.
    // Re-opening a drawer whose columns are already loaded changes nothing, so
    // this is the only thing asking for a preview on that path.
    void store.load().then(this.schedulePreview)
  }

  disconnect(): void {
    document.removeEventListener('turbo:frame-load', this.reapplyPreview)

    this.stopWatchers.forEach((stop) => stop())
    this.stopWatchers = []

    this.teardownPreview()

    this.app?.unmount()
    this.app = null
    this.store = null
  }

  private mountDrawer(): ReturnType<typeof useColumnEditor> {
    if (this.store) return this.store

    this.app = createApp(ColumnEditorDrawer)
    this.app.mount(this.drawerTarget)

    // Resolved here, not in a field initializer: the Pinia instance is a page
    // singleton that gets reset on navigation, so the store must come from the
    // same instance the drawer app was just mounted with.
    const store = useColumnEditor(getPiniaInstance())
    this.store = store

    this.stopWatchers.push(
      // Any change to the list — a value edited, a column added or removed, the
      // order dragged about — is a different set of columns to preview.
      watch(() => store.columns, this.schedulePreview, { deep: true }),
      watch(
        () => store.isOpen,
        (isOpen) => {
          if (!isOpen) this.teardownPreview()
        }
      )
    )

    return store
  }

  private schedulePreview = debounce(
    () => void this.refreshPreview(),
    PREVIEW_DEBOUNCE_MS
  )

  private async refreshPreview(): Promise<void> {
    const store = this.store
    if (!store?.isOpen || !store.reportId) return

    const table = findReportTable()
    if (!table) return

    // A load that failed is a preview that is never coming, and the report has
    // been sitting there saying nothing since the drawer opened (see `open`). So
    // it gets its own columns back and reads as itself again while the drawer
    // offers a retry — which lands here again, with something to show this time.
    if (store.status === 'error') {
      restoreReportColumns(table)
      return
    }

    // Nothing to preview until the drawer knows what the report's columns are.
    // `configure` empties them on the way to loading them, and the watcher below
    // schedules a preview for that — which, if the load is slower than the
    // debounce, would ask what an empty set of columns looks like and answer by
    // collapsing the report to nothing but its row labels. The report stays quiet
    // for that: the columns are on their way.
    if (store.status !== 'loaded') return

    // Nothing previewed means the report's own figures are still on screen, at
    // their own widths — a retried load lands here having just had them given
    // back. They go quiet for the same reason they do on open: so the preview
    // arriving only moves columns about instead of also resizing them.
    if (!this.previewRendered) blankReportColumns(table)

    const request = (this.previewRequest += 1)

    try {
      const { columns } = await fetchColumnsPreviewRequest({
        reportId: store.reportId,
        columns: store.columns.map((column) => ({
          type: column.type,
          values: column.values,
        })),
      })

      // The drawer may have closed, or a later edit may already have been
      // previewed, while this request was in flight.
      if (request !== this.previewRequest || !store.isOpen) return

      renderPreviewColumns(table, columns)
      this.previewRendered = true
    } catch (error) {
      // Whatever was previewed stays on screen: it is one edit out of date,
      // which beats dropping back to real columns the drawer isn't editing. With
      // nothing previewed yet there is nothing to keep, and the report has been
      // sitting there saying nothing — so it gets its own columns back.
      //
      // Superseded requests keep their hands off the table, same as the success
      // path: a newer preview is on its way, and giving the real columns back for
      // it to hide again is the reflow the report was blanked to avoid.
      const stale = request !== this.previewRequest || !store.isOpen
      if (!stale && !this.previewRendered) restoreReportColumns(table)

      SfLog.error('Failed to preview the report columns', {
        error,
        extra: { reportId: store.reportId },
      })
    }
  }

  // The table frame loads lazily and reloads on its own (a period set change, a
  // finished async render), each time replacing the table the preview was
  // applied to. The fresh table shows its own columns until the preview lands on
  // it, same as on open.
  private reapplyPreview = (event: Event): void => {
    if ((event.target as HTMLElement).id !== TABLE_FRAME_ID) return
    if (!this.store?.isOpen) return

    // A fresh table, so nothing is previewed on it yet.
    this.previewRendered = false

    blankReportColumns(findReportTable())

    void this.refreshPreview()
  }

  private teardownPreview(): void {
    this.schedulePreview.cancel()
    this.previewRendered = false
    restoreReportColumns(findReportTable())
  }
}
